import React, { useState, useRef } from 'react';
import Editor from '@monaco-editor/react';
import './CodeEditor.css';

/**
 * CodeEditor - Monaco-based code editor for coding challenges
 * 
 * Props:
 * - language: Programming language (javascript, python, java, etc.)
 * - code: Initial code value
 * - onChange: Callback when code changes
 * - onRun: Callback to execute code
 * - output: Execution output to display
 * - isRunning: Whether code is currently executing
 * - testCases: Array of test cases (optional)
 * - readOnly: Whether editor is read-only
 */
const CodeEditor = ({
  language = 'javascript',
  code = '',
  onChange,
  onRun,
  output = '',
  isRunning = false,
  testCases = [],
  readOnly = false,
  height = '400px'
}) => {
  const [theme, setTheme] = useState('vs-dark');
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const editorRef = useRef(null);

  const supportedLanguages = [
    { id: 'javascript', name: 'JavaScript', monacoId: 'javascript' },
    { id: 'python', name: 'Python', monacoId: 'python' },
    { id: 'java', name: 'Java', monacoId: 'java' },
    { id: 'c++', name: 'C++', monacoId: 'cpp' },
    { id: 'c', name: 'C', monacoId: 'c' },
    { id: 'typescript', name: 'TypeScript', monacoId: 'typescript' },
    { id: 'go', name: 'Go', monacoId: 'go' },
    { id: 'rust', name: 'Rust', monacoId: 'rust' }
  ];

  const themes = [
    { id: 'vs-dark', name: 'Dark' },
    { id: 'light', name: 'Light' },
    { id: 'hc-black', name: 'High Contrast' }
  ];

  const defaultTemplates = {
    javascript: `// JavaScript Solution
function solution(input) {
  // Your code here
  return input;
}

// Test your solution
console.log(solution("Hello"));`,
    
    python: `# Python Solution
def solution(input):
    # Your code here
    return input

# Test your solution
print(solution("Hello"))`,
    
    java: `// Java Solution
public class Solution {
    public static String solution(String input) {
        // Your code here
        return input;
    }
    
    public static void main(String[] args) {
        System.out.println(solution("Hello"));
    }
}`,
    
    'c++': `// C++ Solution
#include <iostream>
#include <string>
using namespace std;

string solution(string input) {
    // Your code here
    return input;
}

int main() {
    cout << solution("Hello") << endl;
    return 0;
}`,
    
    c: `// C Solution
#include <stdio.h>
#include <string.h>

void solution(char* input, char* output) {
    // Your code here
    strcpy(output, input);
}

int main() {
    char input[] = "Hello";
    char output[100];
    solution(input, output);
    printf("%s\\n", output);
    return 0;
}`,
    
    typescript: `// TypeScript Solution
function solution(input: string): string {
  // Your code here
  return input;
}

// Test your solution
console.log(solution("Hello"));`,
    
    go: `// Go Solution
package main

import "fmt"

func solution(input string) string {
    // Your code here
    return input
}

func main() {
    fmt.Println(solution("Hello"))
}`,
    
    rust: `// Rust Solution
fn solution(input: &str) -> String {
    // Your code here
    input.to_string()
}

fn main() {
    println!("{}", solution("Hello"));
}`
  };

  const handleEditorMount = (editor) => {
    editorRef.current = editor;
  };

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    setSelectedLanguage(newLang);
    
    // Optionally set default template when changing language
    if (onChange && (!code || code.trim() === '')) {
      onChange(defaultTemplates[newLang] || '');
    }
  };

  const handleRun = () => {
    if (onRun) {
      onRun(editorRef.current?.getValue() || code, selectedLanguage);
    }
  };

  const getMonacoLanguage = (langId) => {
    const lang = supportedLanguages.find(l => l.id === langId);
    return lang ? lang.monacoId : langId;
  };

  const formatOutput = (outputText) => {
    if (!outputText) return 'No output';
    if (typeof outputText === 'object') {
      return JSON.stringify(outputText, null, 2);
    }
    return outputText;
  };

  return (
    <div className="code-editor-container">
      <div className="code-editor-toolbar">
        <div className="toolbar-left">
          <select 
            value={selectedLanguage} 
            onChange={handleLanguageChange}
            className="language-select"
            disabled={readOnly}
          >
            {supportedLanguages.map(lang => (
              <option key={lang.id} value={lang.id}>{lang.name}</option>
            ))}
          </select>
          
          <select 
            value={theme} 
            onChange={(e) => setTheme(e.target.value)}
            className="theme-select"
          >
            {themes.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        
        <div className="toolbar-right">
          <button 
            className="reset-btn"
            onClick={() => onChange && onChange(defaultTemplates[selectedLanguage] || '')}
            disabled={readOnly}
          >
            Reset
          </button>
          <button 
            className={`run-btn ${isRunning ? 'running' : ''}`}
            onClick={handleRun}
            disabled={isRunning || readOnly}
          >
            {isRunning ? (
              <>
                <span className="spinner"></span>
                Running...
              </>
            ) : (
              <>
                <span className="play-icon">▶</span>
                Run Code
              </>
            )}
          </button>
        </div>
      </div>
      
      <div className="code-editor-main">
        <div className="editor-panel">
          <Editor
            height={height}
            language={getMonacoLanguage(selectedLanguage)}
            value={code || defaultTemplates[selectedLanguage]}
            theme={theme}
            onChange={onChange}
            onMount={handleEditorMount}
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              roundedSelection: true,
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 2,
              wordWrap: 'on',
              readOnly: readOnly,
              padding: { top: 16, bottom: 16 }
            }}
          />
        </div>
        
        <div className="output-panel">
          <div className="output-header">
            <span className="output-title">Output</span>
            {output && (
              <span className={`output-status ${output.includes('Error') ? 'error' : 'success'}`}>
                {output.includes('Error') ? '✗ Error' : '✓ Executed'}
              </span>
            )}
          </div>
          <pre className={`output-content ${output?.includes('Error') ? 'error' : ''}`}>
            {formatOutput(output)}
          </pre>
          
          {testCases.length > 0 && (
            <div className="test-cases-section">
              <div className="test-cases-header">Test Cases</div>
              <div className="test-cases-list">
                {testCases.map((tc, index) => (
                  <div key={index} className={`test-case ${tc.passed ? 'passed' : tc.passed === false ? 'failed' : ''}`}>
                    <span className="test-case-number">Test {index + 1}</span>
                    {tc.passed !== undefined && (
                      <span className="test-case-status">
                        {tc.passed ? '✓ Passed' : '✗ Failed'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodeEditor;
